/* globals login */
import React, { useEffect, useReducer, useContext } from "react";
import CreateRange from "./Create";
import ListRanges from "./List";
import ActionsContext from "common/ActionsContext";
import { parseNTPRanges, parseDeviceIPRanges, getLicenseServers } from "../api";
import { parseClients as parseRADIUSClients, parseServers } from "views/Config/RADIUS/api";
import { parseClients as parseRESTClients } from "views/Config/REST/api";
import { parseManagementInterface } from "views/Config/Interfaces/Management/api";
import IpV4 from "common/ipv4";
import { parseClusterResponse } from "common/api";
import SelectBqnModal from "./SelectBqnModal";
import BqnContext from "common/BqnContext";

const doesNothing = () => {};

const higherId = (current = 0, { __id }) => (current > __id ? current : __id);

const areFrom = (source) => (item) => ({ ...item, from: source });

const addressesAsRangeWithSuffix =
  (suffix = 32) =>
  (list) =>
    list.map(({ address, mask = suffix }) => ({ range: `${address}/${mask}` }));

const wrapAsList = (item) => (item === undefined ? [] : [item]);

const itsRange = ({ range }) => range;

const supressRepetitions = (initial = []) => {
  const repetitions = new Set(initial.map(itsRange));
  return (list) =>
    list.flatMap(({ range, ...rest }) =>
      repetitions.has(range)
        ? []
        : repetitions.add(range) && [{ range, ...rest }]
    );
};

const getClientConnectionAddress = () =>
  Promise.resolve(login.localAddr)
    .then((address) => ({ range: IpV4.normalizeCidr(address) }))
    .then(wrapAsList);

const getOtherBqnsIpAddresses = (bqns, bqn) =>
  Promise.resolve(
    addressesAsRangeWithSuffix(32)(
      bqns
        .filter((item) => parseFloat(item.bqnId) !== parseFloat(bqn))
        .map((item) => ({ address: item.ipAddress }))
    )
  );

const loadSuggestions = (previous = [], bqn, iface, bqns) =>
  Promise.all([
    ifCl
      .run("show system ntp")
      .then(parseNTPRanges)
      .catch(() => {
        return [];
      }),
    ifCl
      .run("show interface management detail")
      .then((response) =>
        parseClusterResponse(response, parseManagementInterface)
      )
      .then((response) => response[bqn])
      .then(wrapAsList)
      .then((list) => [
        ...addressesAsRangeWithSuffix(32)(list).map(
          areFrom("your management I/F network")
        ),
        ...addressesAsRangeWithSuffix(32)(
          list[0]?.nameserver ? [{ address: list[0].nameserver }] : []
        ).map((item) => ({
          ...item,
          fromRaw: "Suggested to allow DNS queries from BQN to this IP",
        })),
      ])
      .catch(() => {
        return [];
      }),
    ifCl
      .run("show api rest client")
      .then(parseRESTClients)
      .then(addressesAsRangeWithSuffix(32))
      .catch(() => {
        return [];
      }),
    ifCl
      .run("show api radius server")
      .then(parseServers)
      .then((server) =>
        server.radiusServerAddress
          ? addressesAsRangeWithSuffix(32)([
              { address: server.radiusServerAddress },
            ])
          : []
      )
      .catch(() => {
        return [];
      }),
    ifCl
      .run("show api radius client")
      .then(parseRADIUSClients)
      .then(addressesAsRangeWithSuffix(32))
      .catch(() => {
        return [];
      }),
    ifCl
      .run("show api billing parameters")
      .then((response) => {
        const parts = response.match(/Server address: (.*)\n/);
        return parts && parts[1] !== "n/a"
          ? addressesAsRangeWithSuffix(32)([{ address: parts[1] }])
          : [];
      })
      .catch(() => {
        return [];
      }),
    getLicenseServers(),
    getClientConnectionAddress(),
    getOtherBqnsIpAddresses(bqns, bqn),
  ])
    .then(
      ([
        ntpServers,
        managementRanges,
        restRanges,
        radiusServers,
        radiusClients,
        billingRanges,
        licenseServers,
        connectionClient,
        otherBqnsInCluster,
      ]) => [
        ...ntpServers.map(areFrom("NTP")),
        ...connectionClient.map(areFrom("your current IP address")),
        ...licenseServers.map(areFrom("license servers")),
        ...managementRanges,
        ...restRanges.map(areFrom("REST API clients")),
        ...radiusServers.map(areFrom("RADIUS API server address")),
        ...radiusClients.map(areFrom("RADIUS API clients")),
        ...billingRanges.map(areFrom("your billing server address")),
        ...otherBqnsInCluster.map(areFrom("other BQNs in cluster")),
      ]
    )
    .then((list) => list.map((item) => ({ ...item, bqn, interface: iface })))
    .then(supressRepetitions(previous));

const nextId = (items) => items.reduce(higherId, 0) + 1;
const anyRangeCollisionWith = (__id, range, bqn) => (existing) =>
  existing.range === range && existing.__id !== __id && parseFloat(existing.bqn) === parseFloat(bqn);

const doVerification = (item, list) => {
  const { range, __id, bqn } = item;

  if (IpV4.isValidCidrOrAddr(item.range) !== true) {
    throw "not a valid IPv4 address or range";
  }
  if (
    list.find(anyRangeCollisionWith(__id, IpV4.toCidr(range), bqn)) !== undefined
  ) {
    throw "range already exists.";
  }
};

const enumerate = (start = 1, extraParams) => {
  let current = start;
  return (item) => {
    const result = {
      ...item,
      __id: current,
      ...extraParams,
    };
    current += 1;
    return result;
  };
};

const mergeSuggestions = (list = [], suggestions = []) => {
  const enumeration = enumerate(nextId(list), { stored: false });
  return [
    ...list,
    ...suggestions.flatMap((suggestion) =>
      list.every((item) => notEqual(suggestion, item))
        ? [enumeration(suggestion)]
        : []
    ),
  ];
};
const notEqual = (one, other) => one.range !== other.range || parseFloat(one.bqn) !== parseFloat(other.bqn);

function rangesReducer({ list }, { type, ...action }) {
  switch (type) {
    case "added": {
      return {
        changed: true,
        list: [
          ...list,
          {
            ...action.item,
            range: IpV4.toCidr(action.item.range),
            stored: false,
            __id: nextId(list),
          },
        ],
      };
    }
    case "deleted": {
      return {
        changed: true,
        list: list.flatMap(({ __id, stored, ...range }) =>
          !action.ids.includes(__id)
            ? [{ __id, stored, ...range }]
            : stored === true
            ? [{ __id, stored, ...range, deleted: true }]
            : []
        ),
      };
    }
    case "merge": {
      return {
        changed: true,
        list: mergeSuggestions(list, action.list),
      };
    }
    default: {
      throw Error("Unknown action: " + type);
    }
  }
}

const Ranges = ({ initial = [], onChange = doesNothing, managementInterfaces }) => {
  const actions = useContext(ActionsContext);
  const {
    bqnId,
    bqns,
    clusterStatus: { mode },
  } = useContext(BqnContext); 
  const [ranges, dispatch] = useReducer(rangesReducer, {
    changed: false,
    list: [...initial],
  });

  const handleDeleteId = (ids) => {
    dispatch({
      type: "deleted",
      ids,
    });
  };
  const createRanges = (item) => {
    doVerification(item, ranges.list);
    loadAndMergeSuggestions([item], item.bqn).then(() =>
      dispatch({
        type: "added",
        item,
      })
    );
  };
  const validateNewItem = (range) => {
    doVerification(range, ranges.list);
  };
  const loadAndMergeSuggestions = (previous = [], bqn) =>
    loadSuggestions(previous, bqn, managementInterfaces[bqn], bqns).then((suggestions) =>
      dispatch({ type: "merge", list: suggestions })
    );

  useEffect(() => {
    ranges.changed === true && onChange(ranges.list);
  }, [ranges]);

  useEffect(() => {
    if (mode === "standalone") {
      return actions.recv("load-suggestions", () =>
        loadAndMergeSuggestions([], bqnId)
      );
    }
  }, []);

  return (
    <>
      <CreateRange
        key={JSON.stringify(ranges.list)}
        doSave={createRanges}
        doValidate={validateNewItem}
        managementInterfaces={managementInterfaces}
      />
      {mode === "cluster" && (
        <SelectBqnModal doSave={(bqn) => loadAndMergeSuggestions([], bqn)} />
      )}
      <ListRanges items={ranges.list} onDelete={handleDeleteId} />
    </>
  );
};

export default Ranges;
